import type { JsonSchemaLike, JsonValue } from "../types";

const isObject = (value: JsonValue): value is Record<string, JsonValue> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const generateLooseSchema = (value: JsonValue): JsonSchemaLike => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { type: "array", items: {} };
    }

    const itemSchemas = value.map((item) => generateLooseSchema(item));
    const uniqueSchemas = itemSchemas.filter(
      (schema, index, arr) => arr.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(schema)) === index,
    );

    if (uniqueSchemas.length === 1) {
      return {
        type: "array",
        items: uniqueSchemas[0],
      };
    }

    return {
      type: "array",
      items: {
        anyOf: uniqueSchemas,
      },
    };
  }

  if (isObject(value)) {
    const properties = Object.entries(value).reduce<Record<string, JsonSchemaLike>>((acc, [key, childValue]) => {
      acc[key] = generateLooseSchema(childValue);
      return acc;
    }, {});

    return {
      type: "object",
      properties,
      // Keep object flexible so new keys can be added by editor users.
      additionalProperties: true,
    };
  }

  if (value === null) {
    return { type: "null" };
  }

  return { type: typeof value };
};

export const validateWithSchema = (
  schema: JsonSchemaLike,
  data: JsonValue,
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const getValueType = (value: JsonValue): string => {
    if (value === null) return "null";
    if (Array.isArray(value)) return "array";
    return typeof value;
  };

  const walk = (currentSchema: JsonSchemaLike, currentValue: JsonValue, path: string): void => {
    if (currentSchema.anyOf && currentSchema.anyOf.length > 0) {
      const isAnySchemaValid = currentSchema.anyOf.some((candidateSchema) => {
        const candidateErrorsBefore = errors.length;
        walk(candidateSchema, currentValue, path);
        const hasNewErrors = errors.length > candidateErrorsBefore;

        if (hasNewErrors) {
          errors.splice(candidateErrorsBefore, errors.length - candidateErrorsBefore);
          return false;
        }

        return true;
      });

      if (!isAnySchemaValid) {
        errors.push(`${path} does not match any allowed schema`);
      }
      return;
    }

    if (currentSchema.type) {
      const currentType = getValueType(currentValue);
      if (currentSchema.type !== currentType) {
        errors.push(`${path} expected ${currentSchema.type} but got ${currentType}`);
        return;
      }
    }

    if (currentSchema.type === "object") {
      if (typeof currentValue !== "object" || currentValue === null || Array.isArray(currentValue)) {
        errors.push(`${path} expected object`);
        return;
      }

      const valueObject = currentValue as Record<string, JsonValue>;
      const schemaProperties = currentSchema.properties ?? {};

      Object.entries(schemaProperties).forEach(([key, childSchema]) => {
        if (Object.prototype.hasOwnProperty.call(valueObject, key)) {
          walk(childSchema, valueObject[key], `${path}/${key}`);
        }
      });

      if (currentSchema.additionalProperties === false) {
        Object.keys(valueObject).forEach((key) => {
          if (!Object.prototype.hasOwnProperty.call(schemaProperties, key)) {
            errors.push(`${path}/${key} is not allowed`);
          }
        });
      }
      return;
    }

    if (currentSchema.type === "array") {
      if (!Array.isArray(currentValue)) {
        errors.push(`${path} expected array`);
        return;
      }

      if (Array.isArray(currentSchema.items)) {
        currentSchema.items.forEach((childSchema, index) => {
          if (index < currentValue.length) {
            walk(childSchema, currentValue[index], `${path}/${index}`);
          }
        });
        return;
      }

      if (currentSchema.items) {
        currentValue.forEach((item, index) => {
          walk(currentSchema.items as JsonSchemaLike, item, `${path}/${index}`);
        });
      }
    }
  };

  walk(schema, data, "$");

  if (errors.length === 0) {
    return { valid: true, errors: [] };
  }

  return { valid: false, errors };
};
